import { CalendarCheck, AtSign, Menu, MessageCircle, Phone, X } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { PublicPage, PublicSiteInfo } from '@barbeiropro/contracts';
import { maskPhone } from '@/lib/format';
import { BlockRenderer, Pole, SiteButton } from './blocks';
import { pagePath, SiteLink, SiteProvider, useSite } from './site-context';
import { displayStyle, groupHours, instaLink, themeVars, useThemeFonts, waLink } from './theme';

function Brand({ inverse }: { inverse?: boolean }) {
  const { site, slug, preview } = useSite();
  const o = site.organization;
  return (
    <Link to={pagePath(slug, '')} onClick={(e) => preview && e.preventDefault()} className="flex min-w-0 items-center gap-3" aria-label={`${o.name}: início`}>
      {o.logoUrl ? (
        <img src={o.logoUrl} alt="" width={44} height={44} className="size-11 shrink-0 object-contain" />
      ) : null}
      <span className="truncate text-2xl" style={{ ...displayStyle, color: inverse ? 'var(--s-on-primary)' : undefined }}>
        {o.name}
      </span>
    </Link>
  );
}

function Header({ pageSlug }: { pageSlug: string }) {
  const { site, slug, preview } = useSite();
  const [open, setOpen] = useState(false);
  const nav = site.nav;
  return (
    <header className="sticky top-0 z-30 border-b border-white/10" style={{ background: 'var(--s-primary)', color: 'var(--s-on-primary)' }}>
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-5 @2xl:px-10">
        <Brand inverse />
        <nav aria-label="Páginas do site" className="hidden items-center gap-1 @4xl:flex">
          {nav.map((p) => (
            <Link
              key={p.slug}
              to={pagePath(slug, p.slug)}
              onClick={(e) => preview && e.preventDefault()}
              aria-current={p.slug === pageSlug ? 'page' : undefined}
              className="px-3 py-2 text-sm font-semibold opacity-80 hover:opacity-100 aria-[current=page]:opacity-100 aria-[current=page]:underline aria-[current=page]:decoration-2 aria-[current=page]:underline-offset-8"
            >
              {p.title}
            </Link>
          ))}
          <SiteButton href="#agendar" className="ml-3 !min-h-10 !px-5">Agendar</SiteButton>
        </nav>
        {nav.length > 0 && (
          <button
            type="button"
            className="inline-flex size-11 items-center justify-center @4xl:hidden"
            aria-expanded={open}
            aria-controls="site-menu"
            aria-label={open ? 'Fechar menu' : 'Abrir menu'}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X aria-hidden /> : <Menu aria-hidden />}
          </button>
        )}
      </div>
      {open && (
        <nav id="site-menu" aria-label="Páginas do site" className="grid border-t border-white/10 px-5 pb-4 @4xl:hidden">
          {nav.map((p) => (
            <Link key={p.slug} to={pagePath(slug, p.slug)} onClick={(e) => (preview ? e.preventDefault() : setOpen(false))} className="py-3 text-lg font-semibold">
              {p.title}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}

function Footer() {
  const { site } = useSite();
  const o = site.organization;
  const br = site.branches[0];
  const wa = waLink(o.whatsapp);
  const ig = instaLink(o.instagram);
  return (
    <footer className="relative px-5 py-14 @2xl:px-10" style={{ background: 'var(--s-primary)', color: 'var(--s-on-primary)' }}>
      <Pole className="absolute inset-x-0 top-0 h-2" />
      <div className="mx-auto grid max-w-6xl gap-10 @4xl:grid-cols-3">
        <div>
          <p className="text-4xl" style={displayStyle}>{o.name}</p>
          {br && (
            <address className="mt-4 text-sm not-italic leading-relaxed opacity-90">
              {br.address.street}, {br.address.number}
              <br />
              {br.address.district} · {br.address.city}/{br.address.state}
            </address>
          )}
        </div>
        {br && br.hours.length > 0 && (
          <div>
            <p className="mb-3 text-sm font-semibold opacity-70">Horário de funcionamento</p>
            <dl className="grid gap-1 text-sm">
              {groupHours(br.hours).map((g) => (
                <div key={g.days} className="flex justify-between gap-4 @4xl:max-w-64">
                  <dt>{g.days}</dt>
                  <dd className="tabular">{g.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}
        <div className="grid content-start gap-3 text-sm">
          <p className="mb-1 text-sm font-semibold opacity-70">Fale com a gente</p>
          {wa && (
            <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:underline">
              <MessageCircle className="size-4" aria-hidden /> WhatsApp {maskPhone(o.whatsapp!)}
            </a>
          )}
          {o.phone && (
            <a href={`tel:+55${o.phone}`} className="inline-flex items-center gap-2 hover:underline">
              <Phone className="size-4" aria-hidden /> {maskPhone(o.phone)}
            </a>
          )}
          {ig && (
            <a href={ig} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:underline">
              <AtSign className="size-4" aria-hidden /> Instagram
            </a>
          )}
          {o.facebook && /^https?:\/\//.test(o.facebook) && (
            <a href={o.facebook} target="_blank" rel="noopener noreferrer" className="hover:underline">Facebook</a>
          )}
          {o.tiktok && /^https?:\/\//.test(o.tiktok) && (
            <a href={o.tiktok} target="_blank" rel="noopener noreferrer" className="hover:underline">TikTok</a>
          )}
        </div>
      </div>
      <p className="mx-auto mt-12 max-w-6xl text-xs opacity-60">© {new Date().getFullYear()} {o.name}. Site e agendamento por BarbeiroPro.</p>
    </footer>
  );
}

/** Barra fixa de agendamento para celular (usa a largura do contêiner, não da janela). */
function MobileBar() {
  const { slug, preview } = useSite();
  return (
    <div className="sticky bottom-0 z-30 border-t-2 p-3 @4xl:hidden" style={{ background: 'var(--s-primary)', borderColor: 'var(--s-accent)' }}>
      <SiteLink href={`/b/${slug}/agendar`} className="flex min-h-12 w-full items-center justify-center gap-2 font-bold" style={{ background: 'var(--s-accent)', color: 'var(--s-on-accent)' }} aria-disabled={preview}>
        <CalendarCheck className="size-5" aria-hidden /> Agendar horário
      </SiteLink>
    </div>
  );
}

/** Moldura do site: tema, cabeçalho, rodapé. Serve o site público e a pré-visualização do editor. */
export function SiteShell({
  slug,
  site,
  pageSlug,
  preview = false,
  hideMobileBar = false,
  children,
}: {
  slug: string;
  site: PublicSiteInfo;
  pageSlug: string;
  preview?: boolean;
  hideMobileBar?: boolean;
  children: ReactNode;
}) {
  useThemeFonts(site.theme.fontPair);
  useEffect(() => {
    if (preview) return;
    const href = site.organization.faviconUrl;
    if (!href) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) link = Object.assign(document.createElement('link'), { rel: 'icon' });
    link.href = href;
    document.head.appendChild(link);
  }, [preview, site.organization.faviconUrl]);
  return (
    <SiteProvider value={{ slug, site, preview, pageSlug }}>
      <div className="@container min-h-full" style={themeVars(site.theme)}>
        <a href="#conteudo" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-white focus:p-3 focus:text-black">
          Ir para o conteúdo
        </a>
        <Header pageSlug={pageSlug} />
        <main id="conteudo">{children}</main>
        <Footer />
        {!hideMobileBar && <MobileBar />}
      </div>
    </SiteProvider>
  );
}

export function PageView({ page }: { page: PublicPage }) {
  return page.blocks.length ? (
    <BlockRenderer blocks={page.blocks} />
  ) : (
    <div className="px-6 py-32 text-center" style={{ color: 'var(--s-muted)' }}>
      <p className="text-3xl" style={displayStyle}>{page.title}</p>
      <p className="mt-3">Esta página ainda não tem conteúdo.</p>
    </div>
  );
}

